import { dateOnly } from '../../lib/format';
import { StatusBadge } from '../ui/Badge';
import {
  UNASSIGNED_LANE,
  cellAt,
  hoursLabel,
  loadFraction,
  type WeekCell,
  type WeekGrid,
} from './weekGrid';

/**
 * The week board (121, ref 23413635) — **rig lanes × days**, not the reference's hours ×
 * days. At a four-van shop the week question is *"who has room Thursday?"*; *"what does 10am
 * look like"* is what the day board is for (120 decision 1, approved).
 *
 * 🔴 **NO CAPACITY CLAIM ANYWHERE ON THIS BOARD.** There is no working-hours model in the
 * schema, so "how full" has no denominator. The bar is each cell against the **busiest cell
 * in view**, and the caption under the grid says so out loud. That caption is load-bearing:
 * remove it and the board silently starts claiming a limit it cannot know.
 */

export interface WeekBoardProps {
  grid: WeekGrid;
  /** The shop's today, `YYYY-MM-DD`, so "today" is the SHOP's and not the browser's. */
  todayIso: string;
  onOpenLane: (laneId: string) => void;
  onOpenCell: (laneId: string, day: string) => void;
}

const isWeekend = (iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return d === 0 || d === 6;
};

const dayHeading = (iso: string) => ({
  weekday: dateOnly(iso, { weekday: 'short' }),
  day: dateOnly(iso, { day: 'numeric' }),
});

/**
 * One cell's three facts (decision 3): a stop count, a load bar, booked hours. Nothing else
 * fits honestly at 112px, and each is a stored fact.
 *
 * 🔴 **An empty cell reads "—" and is NOT a button** (decision 7 + A11). A panel that opens
 * to say "nothing here" is worse than a target that does not invite the click, so the role
 * is dropped entirely — which also takes it out of the keyboard order.
 */
function Cell({
  cell,
  busiest,
  onOpen,
}: {
  cell: WeekCell | null;
  busiest: number;
  onOpen: () => void;
}) {
  const frac = loadFraction(cell, busiest);
  if (!cell || cell.stops === 0) {
    return (
      <div className="flex min-h-[72px] items-center justify-center text-sm text-muted-foreground">
        —
      </div>
    );
  }
  // The AM/PM split of the bar, as shares of this cell's own booked minutes (decision 4).
  const total = cell.amMinutes + cell.pmMinutes;
  const amShare = total > 0 ? (cell.amMinutes / total) * 100 : 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-h-[72px] w-full flex-col justify-center gap-1 rounded-[var(--radius)] px-2 py-1.5 text-left hover:bg-surface-2"
    >
      <span className="text-sm font-semibold tabular-nums">
        {cell.stops} {cell.stops === 1 ? 'stop' : 'stops'}
      </span>
      {/*
        The load bar. `frac` is null when nothing is booked — a zero-length bar dressed as
        data is the same lie "—" exists to avoid, so nothing is drawn at all.
      */}
      {frac !== null ? (
        <span
          className="flex h-1.5 w-full overflow-hidden rounded-full bg-surface-2"
          aria-hidden="true"
        >
          <span
            className="h-full bg-brand"
            style={{ width: `${frac * 100 * (amShare / 100)}%` }}
          />
          <span
            className="h-full bg-brand/50"
            style={{ width: `${frac * 100 * (1 - amShare / 100)}%` }}
          />
        </span>
      ) : null}
      <span className="text-xs tabular-nums text-muted-foreground">
        {hoursLabel(cell.bookedMinutes)}
      </span>
    </button>
  );
}

export function WeekBoard({ grid, todayIso, onOpenLane, onOpenCell }: WeekBoardProps) {
  return (
    <div>
      {/*
        ⚠️ The grid scrolls INSIDE its own container — the page body never scrolls sideways.
        Seven day columns plus a lane header does not fit a laptop at every zoom, and a board
        that widens the document breaks every other pane on the page.
      */}
      <div className="hidden overflow-x-auto md:block">
        <div className="min-w-[52rem]">
          <div
            className="grid border-b border-border"
            style={{ gridTemplateColumns: '12rem repeat(7, minmax(6.5rem, 1fr))' }}
          >
            <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Rig
            </div>
            {grid.days.map((d) => {
              const h = dayHeading(d);
              return (
                <div
                  key={d}
                  className={`px-2 py-2 text-center ${
                    d === todayIso ? 'bg-brand/10' : isWeekend(d) ? 'bg-surface-2/40' : ''
                  }`}
                >
                  <p className="text-xs font-semibold">{h.weekday}</p>
                  <p className="text-xs tabular-nums text-muted-foreground">{h.day}</p>
                </div>
              );
            })}
          </div>

          {grid.lanes.map((lane) => (
            <div
              key={lane.laneId}
              className={`grid border-b border-border ${
                lane.laneId === UNASSIGNED_LANE ? 'bg-warn/10' : ''
              }`}
              style={{ gridTemplateColumns: '12rem repeat(7, minmax(6.5rem, 1fr))' }}
            >
              <div className="px-3 py-2">
                <button
                  type="button"
                  onClick={() => onOpenLane(lane.laneId)}
                  className="block w-full truncate text-left text-sm font-semibold hover:underline"
                >
                  {lane.name}
                </button>
                {/*
                  Decision 8: a tech with no van still gets a lane, and the absence is drawn.
                  ⚠️ `vanless` means no van on ANY day this week — a tech vanless on Thursday
                  alone is a Thursday problem, and the day panel is where Thursday is described.
                */}
                {lane.vanless && lane.userId ? (
                  <StatusBadge tone="warn">No van assigned</StatusBadge>
                ) : null}
              </div>
              {grid.days.map((d) => (
                <div
                  key={d}
                  className={
                    d === todayIso ? 'bg-brand/5' : isWeekend(d) ? 'bg-surface-2/30' : ''
                  }
                >
                  <Cell
                    cell={cellAt(grid, lane.laneId, d)}
                    busiest={grid.busiestMinutes}
                    onOpen={() => onOpenCell(lane.laneId, d)}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/*
        🔴 Below 768 the grid is REPLACED, not squeezed (decision 9). Seven columns at 390px
        is 40px a column, which is not a board — it is a smear. Each day card lists only the
        rigs that actually have work that day, so a quiet Tuesday is one line instead of a
        column of dashes.
      */}
      <div className="space-y-3 md:hidden">
        {grid.days.map((d) => {
          const busy = grid.lanes
            .map((lane) => ({ lane, cell: cellAt(grid, lane.laneId, d) }))
            .filter((x) => x.cell && x.cell.stops > 0);
          const h = dayHeading(d);
          return (
            <section
              key={d}
              className={`rounded-[var(--radius)] border border-border p-3 ${
                d === todayIso ? 'border-brand bg-brand/5' : 'bg-surface'
              }`}
            >
              <h3 className="mb-2 font-heading text-sm font-bold">
                {h.weekday} {h.day}
              </h3>
              {busy.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing booked</p>
              ) : (
                <ul className="space-y-1">
                  {busy.map(({ lane, cell }) => (
                    <li key={lane.laneId}>
                      <button
                        type="button"
                        onClick={() => onOpenCell(lane.laneId, d)}
                        className="flex min-h-11 w-full items-center justify-between gap-2 rounded-[var(--radius)] px-2 text-left text-sm hover:bg-surface-2"
                      >
                        <span className="truncate">{lane.name}</span>
                        <span className="shrink-0 tabular-nums text-muted-foreground">
                          {cell!.stops} · {hoursLabel(cell!.bookedMinutes)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {/*
        🔴 THE CAPTION IS PART OF THE DESIGN, not decoration. The bar's denominator is the
        busiest cell IN VIEW, so a quiet week makes small numbers look large. Saying so is
        what keeps the board honest in the absence of a capacity model.
      */}
      <p className="px-1 pt-3 text-xs text-muted-foreground">
        Bars compare each day to the busiest day shown. Closed and canceled work is not
        counted, and no working-hours limit exists to measure against.
      </p>
    </div>
  );
}
